/**
 * โครงสร้างค่าตอบแทน (Compensation) — OWNER/HR. SoT (ADR 0003): PayPilot owns ALL
 * compensation. Four tabs over /hr/comp/*:
 *   - ระดับเงินเดือน   : salary bands (min/mid/max per position)
 *   - โครงสร้างเงินเดือน: pay grid (department × level) + AI-fill
 *   - สวัสดิการ        : benefit packages + assign to employees
 *   - ค่าตอบแทนรวม     : total-comp packages (CTC) + variable pay designs
 *
 * Eval Pilot no longer manages comp — it reads roster/org only (HRD-only).
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Banknote, Grid3x3, Gift, Layers, Sparkles, LineChart } from 'lucide-react';
import { BandsTab } from './comp/BandsTab';
import { StructureTab } from './comp/StructureTab';
import { BenefitsTab } from './comp/BenefitsTab';
import { PackagesTab } from './comp/PackagesTab';
import { TemplatesTab } from './comp/TemplatesTab';
import { SalaryAnalysisTab } from './comp/SalaryAnalysisTab';
import { useMyCapabilities } from '../../hooks';
import { useAuthStore } from '../../lib/store';

type Tab = 'bands' | 'structure' | 'benefits' | 'packages' | 'templates' | 'analysis';

export function OwnerCompensation() {
  const [tab, setTab] = useState<Tab>('bands');
  // แท็บวิเคราะห์เงินเดือน = เฉพาะเจ้าของหรือ HR สิทธิ์เต็ม (edit_salary)
  const role = useAuthStore((s) => s.profile?.role);
  const { data: caps } = useMyCapabilities();
  const canAnalyze = role === 'OWNER' || (caps?.includes('edit_salary') ?? false);

  return (
    <AdminLayout title="โครงสร้างค่าตอบแทน" subtitle="กรอบเงินเดือน สวัสดิการ และค่าตอบแทนรวมตามตำแหน่ง">
      <div className="space-y-4">
        <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
          <TabBtn active={tab === 'bands'} onClick={() => setTab('bands')} icon={<Banknote className="h-4 w-4" />}>ระดับเงินเดือน</TabBtn>
          <TabBtn active={tab === 'structure'} onClick={() => setTab('structure')} icon={<Grid3x3 className="h-4 w-4" />}>โครงสร้างเงินเดือน</TabBtn>
          <TabBtn active={tab === 'benefits'} onClick={() => setTab('benefits')} icon={<Gift className="h-4 w-4" />}>สวัสดิการ</TabBtn>
          <TabBtn active={tab === 'packages'} onClick={() => setTab('packages')} icon={<Layers className="h-4 w-4" />}>ค่าตอบแทนรวม</TabBtn>
          {canAnalyze && <TabBtn active={tab === 'analysis'} onClick={() => setTab('analysis')} icon={<LineChart className="h-4 w-4" />}>วิเคราะห์เงินเดือน</TabBtn>}
          <TabBtn active={tab === 'templates'} onClick={() => setTab('templates')} icon={<Sparkles className="h-4 w-4" />}>แม่แบบ</TabBtn>
        </div>
        {tab === 'bands' && <BandsTab />}
        {tab === 'structure' && <StructureTab />}
        {tab === 'benefits' && <BenefitsTab />}
        {tab === 'packages' && <PackagesTab />}
        {tab === 'analysis' && canAnalyze && <SalaryAnalysisTab />}
        {tab === 'templates' && <TemplatesTab />}
      </div>
    </AdminLayout>
  );
}

function TabBtn({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm ${
        active ? 'border-teal-500 font-medium text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}
